/**
 * Client KYB 与旧 Merchant 数据结构的适配层
 * 用于将新合规 API（SegmentVo/SegmentDto）与现有页面（MerchantInfoData）桥接
 */
import { normalizeComplianceFileResourceUrl } from '@/services/upload/normalizeComplianceFileUrl';
import type { CommFile, SegmentCredential, SegmentDto, SegmentQuestionnaire, SegmentVo } from './clientKybTypes';
import type { FileItem, MerchantInfoData } from './types';

function normalizeFileUrl(url?: string): string | undefined {
  if (!url) return url;
  return normalizeComplianceFileResourceUrl(url);
}

function mapFilesUrls<T extends { url?: string }>(items: T[]): T[] {
  return items.map((item) => ({
    ...item,
    url: normalizeFileUrl(item.url) ?? item.url,
  })) as T[];
}

/** SegmentVo.status -> MerchantInfoData.state */
const statusToStateMap: Record<string, MerchantInfoData['state']> = {
  passed: 'complete',
  rejected: 'reject',
  mitigated: 'reject',
  pending: 'onboarding',
  processing: 'onboarding',
};

/** MerchantInfoData.state -> SegmentDto.status */
const stateToStatusMap: Record<string, SegmentDto['status']> = {
  complete: 'passed',
  reject: 'rejected',
  onboarding: 'pending',
};

/** SegmentCredential.files -> FileItem[] */
function credentialToFiles(cred?: SegmentCredential): FileItem[] | undefined {
  if (!cred?.files?.length) return undefined;
  return mapFilesUrls(cred.files as FileItem[]);
}

/** FileItem[] 或 { url, name }[] -> SegmentCredential */
function filesToCredential(files?: FileItem[] | { url?: string; name?: string }[]): SegmentCredential | undefined {
  if (!files?.length) return undefined;
  const list = files.map((f) => {
    const rawUrl = f.url ?? (f as any).url;
    return {
      url: rawUrl != null ? normalizeComplianceFileResourceUrl(String(rawUrl)) : rawUrl,
      name: f.name ?? (f as any).name,
      uid: (f as any).uid,
      time: (f as any).time,
    };
  });
  return { files: list as CommFile[] };
}

/** SegmentQuestionnaire -> { files } for MerchantInfoData (questionAttachment 等) */
function questionnaireToFiles(q?: SegmentQuestionnaire): FileItem[] | undefined {
  if (!q?.files?.length) return undefined;
  return mapFilesUrls(q.files as FileItem[]);
}

function questionnaireFromFiles(files?: FileItem[]): SegmentQuestionnaire | undefined {
  if (!files?.length) return undefined;
  return { files: mapFilesUrls(files as FileItem[]) as CommFile[], status: 'pending' };
}

/** SegmentVo -> MerchantInfoData */
export function segmentVoToMerchantInfo(vo?: SegmentVo): MerchantInfoData | undefined {
  if (!vo) return undefined;
  return {
    id: vo.id,
    address: vo.address,
    fillStep: vo.fillStep,
    state: statusToStateMap[vo.status || ''] ?? 'onboarding',
    version: '1.0',
    materialSetting: vo.materialSetting,
    ownLicense: (vo as any).ownLicense ?? vo.previewAccount,
    name: vo.name,
    country: vo.country,
    companyName: vo.name,
    companyWeb: vo.companyWeb,
    industry: vo.industry,
    industryName: vo.industryName,
    registrationNumber: vo.registrationNumber,
    registrationDate: vo.registrationDate,
    reviewStep: vo.reviewStep,
    failMessage: vo.message,
    ndaSign: vo.ndaSign,
    ndaSignStatus: vo.ndaSignStatus,
    question1: vo.question1,
    question1PreviewMessage: vo.question1PreviewMessage,
    question1PreviewStatus: vo.question1PreviewStatus,
    question1SignStatus: vo.question1SignStatus,
    question2: vo.question2,
    question2PreviewMessage: vo.question2PreviewMessage,
    question2PreviewStatus: vo.question2PreviewStatus,
    question2SignStatus: vo.question2SignStatus,
    question3: vo.question3,
    productList: vo.catalog,
    businessArea: vo.businessAreas,
    previewAccount: vo.previewAccount,
    previewQuestion1: vo.previewQuestion1,
    previewQuestion2: vo.previewQuestion2,
    businessLicense: credentialToFiles(vo.businessLicense),
    businessRegistrationCert: credentialToFiles(vo.businessRegistrationCert),
    annualReturn: credentialToFiles(vo.annualReturn),
    articlesOfAssociation: credentialToFiles(vo.articlesOfAssociation),
    officePhotos: credentialToFiles(vo.officePhotos),
    shareholdingStructureMaterial: credentialToFiles(vo.shareholdingStructureMaterial),
    uboInfoMaterial: credentialToFiles(vo.uboInfoMaterial),
    uboPersonalInfos: vo.uboPersonalInfos?.map((row) => {
      const rawFiles = row.files;
      return {
        ...row,
        files: rawFiles?.length ? mapFilesUrls(rawFiles as FileItem[]) : undefined,
      };
    }),
    uboCredential: credentialToFiles(vo.uboCredential),
    mtoLicensePlate: credentialToFiles(vo.mtoLicensePlate),
    amlPolicy: credentialToFiles(vo.amlPolicy),
    riskAssessmentReport: credentialToFiles(vo.riskAssessmentReport),
    boardResolution: credentialToFiles(vo.boardResolution),
    complianceOfficerResume: credentialToFiles(vo.complianceOfficerResume),
    goodsPi: credentialToFiles(vo.goodsPi),
    goodsLogistics: credentialToFiles(vo.goodsLogistics),
    serviceContract: credentialToFiles(vo.serviceContract),
    salaryPayroll: credentialToFiles(vo.salaryPayroll),
    familySupportProof: credentialToFiles(vo.familySupportProof),
    enterpriseStaffList: credentialToFiles(vo.enterpriseStaffList),
    enterpriseStaffContact: credentialToFiles(vo.enterpriseStaffContact),
    /** 仅映射问卷附属材料；勿用 dueDiligenceForm 回退，否则「生成并保存」后的问卷 PDF 会错误出现在第三步「问卷附件」中 */
    questionAttachment: questionnaireToFiles(vo.questionnaireAffiliateFile),
  } as MerchantInfoData;
}

/** MerchantInfoData / PostOpeningRequest -> SegmentDto */
export function merchantInfoToSegmentDto(data: Record<string, any>): SegmentDto {
  // 兜底：拒绝态下用户重新提交时，前端把 status 改回 'pending'，避免后端因状态未自动迁移而停留在拒绝态。
  // 同时清空 failMessage，防止把上一次的拒绝原因继续回写给后端。
  // TODO: 后端在 /client/compliance/kyb/update 中根据 fillStatus=completed 自动迁移状态后，移除本兜底。
  const isResubmittingAfterReject = data.state === 'reject';
  const mappedStatus = data.state ? stateToStatusMap[data.state] : data.status;
  const dto: SegmentDto = {
    id: data.id,
    name: data.name,
    address: data.address,
    country: data.country,
    companyWeb: data.companyWeb,
    industry: data.industry,
    industryName: data.industryName,
    registrationNumber: data.registrationNumber,
    registrationDate: data.registrationDate,
    fillStep: data.fillStep,
    reviewStep: data.reviewStep,
    status: isResubmittingAfterReject ? 'pending' : mappedStatus,
    message: isResubmittingAfterReject ? undefined : data.failMessage,
    ndaSign: data.ndaSign,
    ndaSignStatus: data.ndaSignStatus,
    businessAreas: data.businessArea || data.businessAreas,
    catalog: data.productList || data.catalog,
    businessLicense: filesToCredential(data.businessLicense),
    businessRegistrationCert: filesToCredential(data.businessRegistrationCert),
    annualReturn: filesToCredential(data.annualReturn),
    articlesOfAssociation: filesToCredential(data.articlesOfAssociation),
    officePhotos: filesToCredential(data.officePhotos),
    shareholdingStructureMaterial: filesToCredential(data.shareholdingStructureMaterial),
    uboInfoMaterial: filesToCredential(data.uboInfoMaterial),
    uboCredential: filesToCredential(data.uboCredential),
    mtoLicensePlate: filesToCredential(data.mtoLicensePlate),
    amlPolicy: filesToCredential(data.amlPolicy),
    riskAssessmentReport: filesToCredential(data.riskAssessmentReport),
    boardResolution: filesToCredential(data.boardResolution),
    complianceOfficerResume: filesToCredential(data.complianceOfficerResume),
    goodsPi: filesToCredential(data.goodsPi),
    goodsLogistics: filesToCredential(data.goodsLogistics),
    serviceContract: filesToCredential(data.serviceContract),
    salaryPayroll: filesToCredential(data.salaryPayroll),
    familySupportProof: filesToCredential(data.familySupportProof),
    enterpriseStaffList: filesToCredential(data.enterpriseStaffList),
    enterpriseStaffContact: filesToCredential(data.enterpriseStaffContact),
    question1: data.question1,
    question2: data.question2,
    question3: data.question3,
    question1PreviewMessage: data.question1PreviewMessage,
    question1PreviewStatus: data.question1PreviewStatus,
    question2PreviewMessage: data.question2PreviewMessage,
    question2PreviewStatus: data.question2PreviewStatus,
    dueDiligenceForm:
      Array.isArray(data.dueDiligenceForm) ? questionnaireFromFiles(data.dueDiligenceForm) : data.dueDiligenceForm,
    amlCtfScpQuestionnaire: data.amlCtfScpQuestionnaire,
    questionnaireAffiliateFile: filesToCredential(data.questionAttachment),
    previewAccount: data.previewAccount,
    previewQuestion1: normalizeFileUrl(data.previewQuestion1),
    previewQuestion2: normalizeFileUrl(data.previewQuestion2),
  };
  if (Array.isArray(data.uboPersonalInfos) && data.uboPersonalInfos.length > 0) {
    dto.uboPersonalInfos = data.uboPersonalInfos.map((row: Record<string, any>) => {
      const cred = filesToCredential(row.files);
      const { files: _omit, ...rest } = row;
      return {
        ...rest,
        ...(cred?.files?.length ? { files: cred.files } : {}),
      };
    });
  }
  if (data.ownLicense !== undefined) (dto as any).ownLicense = data.ownLicense;
  return dto;
}
